import { resolveFirstLocationMatch } from './locationResolver';

export const DEFAULT_CLIENT_PROMO_CITIES = ['Armenia', 'Manizales', 'Pereira', 'Cali'];
export const DEFAULT_CLIENT_PROMO_PROPERTY_TYPES = ['Apartamento', 'Apartaestudio', 'Casa'];

/**
 * Verifica si una ciudad aplica para la promoción de Crédito Davivienda 8%
 */
export function isDaviviendaPromoCity(city?: string): boolean {
  if (!city) return false;
  const normalized = city
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  return (
    normalized.includes('armenia') ||
    normalized.includes('manizales') ||
    normalized.includes('pereira') ||
    normalized.includes('cali')
  );
}

/**
 * Verifica si un tipo de inmueble aplica para la promoción de Crédito Davivienda 8%
 * (Apartamentos, apartaestudios y casas)
 */
export function isDaviviendaPromoPropertyType(propertyType?: string): boolean {
  if (!propertyType) return false;
  const normalized = propertyType
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  return (
    normalized === 'apartamento' ||
    normalized === 'apartaestudio' ||
    normalized === 'casa'
  );
}

/**
 * Verifica si un inmueble cumple con los criterios de la promoción Crédito Davivienda 8%
 * (Ubicación en Armenia, Manizales, Pereira o Cali Y tipo Apartamento, Apartaestudio o Casa)
 */
export function isDaviviendaPromoProperty(city?: string, propertyType?: string): boolean {
  return isDaviviendaPromoCity(city) && isDaviviendaPromoPropertyType(propertyType);
}

/**
 * Obtiene la lista de nombres canónicos de las ciudades de Armenia, Manizales, Pereira y Cali
 * para los filtros iniciales del cliente.
 */
export function getClientDefaultPromoCities(allLocations: { name: string }[]): string[] {
  if (!allLocations || allLocations.length === 0) {
    return ['ARMENIA', 'MANIZALES', 'PEREIRA', 'CALI'];
  }

  const resolved = DEFAULT_CLIENT_PROMO_CITIES.map((target) => {
    const match = resolveFirstLocationMatch(target, allLocations);
    if (match) return match;

    const fallback = allLocations.find((l) => {
      const lower = l.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
      const targetLower = target.toLowerCase();
      return lower === targetLower || lower.startsWith(targetLower);
    });

    return fallback ? fallback.name : target.toUpperCase();
  });

  return Array.from(new Set(resolved));
}

/**
 * Obtiene los nombres de tipos de inmuebles promocionales (Apartamento, Apartaestudio, Casa)
 * disponibles en el sistema para los filtros iniciales del cliente.
 */
export function getClientDefaultPromoPropertyTypes(availableTypes: { name: string }[]): string[] {
  if (!availableTypes || availableTypes.length === 0) {
    return DEFAULT_CLIENT_PROMO_PROPERTY_TYPES;
  }

  const matched = availableTypes
    .filter((t) => isDaviviendaPromoPropertyType(t.name))
    .map((t) => t.name);

  return matched.length > 0 ? matched : DEFAULT_CLIENT_PROMO_PROPERTY_TYPES;
}

/**
 * Asegura que una lista de ubicaciones mantenga siempre las ciudades promocionales obligatorias del cliente.
 */
export function ensureClientPromoCities(selectedUbicacion: string[], allLocations?: { name: string }[]): string[] {
  const baseCities = getClientDefaultPromoCities(allLocations || []);
  return Array.from(new Set([...baseCities, ...selectedUbicacion]));
}

/**
 * Asegura que una lista de tipos mantenga siempre los tipos promocionales obligatorios del cliente.
 */
export function ensureClientPromoPropertyTypes(selectedTypes: string[], availableTypes?: { name: string }[]): string[] {
  const baseTypes = getClientDefaultPromoPropertyTypes(availableTypes || []);
  return Array.from(new Set([...baseTypes, ...selectedTypes]));
}

/**
 * Resolves the canonical name for Bogotá based on the available locations list in the system.
 */
export function getBogotaCanonicalName(allLocations: { name: string }[]): string {
  if (allLocations && allLocations.length > 0) {
    const match = resolveFirstLocationMatch('Bogota', allLocations);
    if (match) return match;

    const fallback = allLocations.find(l => {
      const lower = l.name.toLowerCase().trim();
      return (
        lower === 'bogotá, d.c.' ||
        lower === 'bogota, d.c.' ||
        lower === 'bogotá' ||
        lower === 'bogota' ||
        lower.startsWith('bogot')
      );
    });

    if (fallback) return fallback.name;
  }

  return 'BOGOTÁ, D.C.';
}
